/**
 * PieChart Component
 * Displays pie chart data with click handling for drill-down
 */

import React from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Box } from '@mui/material';
import type { PieChartDataDto } from '@/api/types/dtos/analytics/AnalyticsDto';

ChartJS.register(ArcElement, Title, Tooltip, Legend);

interface PieChartProps {
  data: PieChartDataDto;
  onClick: (value: string) => void;
}

export const PieChart: React.FC<PieChartProps> = ({ data, onClick }) => {
  // Safety check for data
  if (!data || !data.labels || !data.values || data.labels.length === 0) {
    return (
      <Box
        sx={{
          height: 320,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-secondary)',
        }}
      >
        No data available
      </Box>
    );
  }

  const chartData = {
    labels: data.labels,
    datasets: [
      {
        data: data.values,
        backgroundColor: data.colors || ['#1976d2', '#388e3c', '#fbc02d', '#d32f2f', '#7b1fa2'],
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: 20,
    },
    spacing: 0, // Remove gaps between slices
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        const value = data.labels[index];
        onClick(value);
      }
    },
    onHover: (event: any, elements: any[]) => {
      event.native.target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
    },
    interaction: {
      mode: 'nearest' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
          // Handle multi-line labels: replace \n with space in legend
          generateLabels: (chart: any) => {
            const data = chart.data;
            if (data.labels && data.datasets.length) {
              return data.labels.map((label: string, i: number) => ({
                text: label.replace(/\n/g, ' '),
                fillStyle: data.datasets[0].backgroundColor[i],
                strokeStyle: '#fff',
                lineWidth: 1,
                hidden: false,
                index: i,
                pointStyle: 'circle',
              }));
            }
            return [];
          },
        },
      },
      tooltip: {
        enabled: true,
        mode: 'nearest' as const,
        intersect: false,
        // Larger hit area for small slices
        hitRadius: 30,
        hoverRadius: 20,
        callbacks: {
          label: (context: any) => {
            // For pie charts, parsed is the value directly (not an object with .raw)
            const value = context.parsed ?? context.raw ?? 0;
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            // Handle multi-line labels: replace \n with space
            const displayLabel = context.label.replace(/\n/g, ' ');

            // Safety check for division by zero
            if (total === 0) return `${displayLabel}: ${value} farms (0%)`;

            const percentage = ((value / total) * 100).toFixed(1);
            return `${displayLabel}: ${value} farms (${percentage}%)`;
          },
        },
      },
    },
  };

  return (
    <Box sx={{ height: 320, width: '100%', maxWidth: 600, mx: 'auto', position: 'relative' }}>
      <Pie options={options} data={chartData} />
    </Box>
  );
};
